import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import app, { auth, googleProvider, signInWithPopup } from "../firebase";
import { getFirestore, collection, addDoc, query, where, getDocs, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

import ReCAPTCHA from "react-google-recaptcha";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';

import { TextField, Button, Paper, Typography, MenuItem, Select, InputLabel, FormControl, Box,  Radio,
  RadioGroup,
  FormControlLabel,
  Avatar,
  FormLabel,
} from "@mui/material";

// Styled component for the file input
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function AddMemberForm() {
  const [formData, setFormData] = useState({
    salutation: "",
    firstName: "",
    surname: "",
    mobileNo: "",
    alternateContactNo: "",
    professionalEmail: "",
    personalEmail: "",
    dob: "",
    membershipCategory: "",
    resiAddress1: "",
    resiAddress2: "",
    city: "",
    pincode: "",
    gender: "",
    qualification: "",
    organisation: "",
    noOfEmployees: "",
    designation: "",
    experience: "",
    expertise: "",
    paymentRef: "",
    paymentAmount: "",
    linkedIn: "",
    facebook: "",
    instagram: "",
    referralSource: "",
    additionalComments: "",
    membershipStartDate: "",
    membershipEndDate: "",
    purposeOfJoining: ""
  });

  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const captchaRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const linkTenantId = searchParams.get("t") || "";
  const linkTenantName = searchParams.get("n") || "";

  const saveMembershipDraft = () => {
    const draft = {
      formData,
      photoPreview,
      linkTenantId,
      linkTenantName,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem("membershipFormDraft", JSON.stringify(draft));
  };

  useEffect(() => {
    const draft = localStorage.getItem("membershipFormDraft");
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed.formData) {
          setFormData((prev) => ({ ...prev, ...parsed.formData }));
        }
        if (parsed.photoPreview) {
          setPhotoPreview(parsed.photoPreview);
        }
      } catch (error) {
        console.error("Failed to load membership draft:", error);
      }
    }
  }, []);

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert("Photo size should not exceed 5MB");
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert("Please upload an image file");
        return;
      }

      setSelectedPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

 const handleGoogleSignIn = async () => {
  if (!captchaValue) {
    setErrorMessage("Please verify that you are not a robot.");
    return;
  }

  try {
    const result = await signInWithPopup(auth, googleProvider);

    setFormData((prevFormData) => ({
      ...prevFormData,
      personalEmail: result.user.email
    }));

    setIsSignedIn(true);
    setErrorMessage("");
  } catch (error) {
    console.error("Google Sign-In Error:", error);
  }
};


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaValue) {
      setErrorMessage("Please complete the ReCAPTCHA verification.");
      return;
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.mobileNo)) {
      alert("Mobile number must be 10 digits long.");
      return;
    }

    // Email validation: must match the pattern example@example.com
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.professionalEmail)) {
      alert("Please enter a valid professional email.");
      return;
    }

    if (formData.personalEmail && !emailRegex.test(formData.personalEmail)) {
      alert("Please enter a valid personal email.");
      return;
    }

    const userAuthenticated = !!(
      auth.currentUser ||
      sessionStorage.getItem("otpLoggedIn") === "true" ||
      sessionStorage.getItem("userEmail")
    );

    if (!userAuthenticated) {
      saveMembershipDraft();
      const returnUrl = `${location.pathname}${location.search}`;
      alert("Your membership details are saved. Please log in to complete the submission.");
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }

    try {
      const db = getFirestore();
      const storage = getStorage(app);
      const requestsRef = collection(db, "MembershipRequests");
      let photoURL = null;

      // Upload photo if selected
      if (selectedPhoto) {
        const photoRef = ref(storage, `member-photos/${formData.personalEmail}/${selectedPhoto.name}`);
        await uploadBytes(photoRef, selectedPhoto);
        photoURL = await getDownloadURL(photoRef);

        // Add photo reference to photos collection
        const photosRef = collection(db, "MemberPhotos");
        await addDoc(photosRef, {
          memberEmail: formData.personalEmail,
          photoURL: photoURL,
          uploadDate: new Date().toISOString(),
          fileName: selectedPhoto.name,
          tenantId: sessionStorage.getItem("tenantId") // Add tenant context to photos
        });
      }

      // Query to check if a document with the same personalEmail already exists
      const q = query(requestsRef, where("personalEmail", "==", formData.personalEmail));
      const querySnapshot = await getDocs(q);

      // Get current user's tenant context or tenant from shared link
      const currentTenantId = sessionStorage.getItem("tenantId") || linkTenantId || null;

      const memberDataWithPhoto = {
        ...formData,
        photoURL: photoURL,
        status: "pending",
        tenantId: currentTenantId // Add tenant context to membership request
      };

      if (!querySnapshot.empty) {
        // Update existing document
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, memberDataWithPhoto);
        alert("Request updated successfully!");
      } else {
        // Add new document
        await addDoc(requestsRef, memberDataWithPhoto);
        alert("Request submitted successfully!");
      }

      // Reset form
      setFormData({
        salutation: "",
        firstName: "",
        surname: "",
        mobileNo: "",
        alternateContactNo: "",
        professionalEmail: "",
        personalEmail: "",
        dob: "",
        membershipCategory: "",
        resiAddress1: "",
        resiAddress2: "",
        city: "",
        pincode: "",
        gender: "",
        qualification: "",
        organisation: "",
        noOfEmployees: "",
        designation: "",
        experience: "",
        expertise: "",
        paymentRef: "",
        paymentAmount: "",
        linkedIn: "",
        facebook: "",
        instagram: "",
        referralSource: "",
        additionalComments: "",
        membershipStartDate: "",
        membershipEndDate: "",
        purposeOfJoining: ""
      });
      localStorage.removeItem("membershipFormDraft");
      setSelectedPhoto(null);
      setPhotoPreview(null);
      setIsSignedIn(false);
      setCaptchaValue(null);
      captchaRef.current.reset();
      setErrorMessage(null);
    } catch (error) {
      console.error("Error submitting request:", error);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
      <Paper elevation={3} sx={{ width: "100%", maxWidth: 600, p: 4 }}>
        <Typography variant="h5" align="center" gutterBottom>
          Membership Form
        </Typography>
        {/*{!isSignedIn && (
          <div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGoogleSignIn}
            sx={{ mb: 2, width: "100%" }}
          >
            Sign up with Google
          </Button>

          <ReCAPTCHA
            sitekey= {process.env.REACT_APP_SITE_KEY}
            onChange={(value) => setCaptchaValue(value)}
            sx={{ mb: 2, width: "100%" }}
          />

          {errorMessage && <Typography color="error">{errorMessage}</Typography>}
        </div>
        )}
        {isSignedIn && (*/}
          <form onSubmit={handleSubmit}>
            {/* Photo Upload Section */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              mb: 3, 
              mt: 2 
            }}>
              <Avatar
                src={photoPreview}
                sx={{
                  width: 120,
                  height: 120,
                  mb: 2,
                  border: '2px solid #1976d2'
                }}
              />
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                Upload Photo
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
              </Button>
              <Typography variant="caption" color="textSecondary">
                Max size: 5MB. Supported formats: JPG, PNG
              </Typography>
            </Box>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Salutation</InputLabel>
              <Select
                name="salutation"
                value={formData.salutation}
                onChange={handleChange}
                label="Salutation"
              >
                <MenuItem value="Mr">Mr</MenuItem>
                <MenuItem value="Ms">Ms</MenuItem>
                <MenuItem value="Mrs">Mrs</MenuItem>
                <MenuItem value="Mas">Mas</MenuItem>
                <MenuItem value="Jr">Jr</MenuItem>
                <MenuItem value="Sr">Sr</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Surname"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Mobile No"
              name="mobileNo"
              value={formData.mobileNo}
              onChange={handleChange}
              inputProps={{ maxLength: 10, pattern: '[0-9]*', inputMode: 'numeric' }}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Alternate Contact No"
              name="alternateContactNo"
              value={formData.alternateContactNo}
              onChange={handleChange}
              inputProps={{ maxLength: 10, pattern: '[0-9]*', inputMode: 'numeric' }}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Professional Email"
              name="professionalEmail"
              value={formData.professionalEmail}
              onChange={handleChange}
              fullWidth
              required
              type="email"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Personal Email"
              name="personalEmail"
              value={formData.personalEmail}
              onChange={handleChange}
              fullWidth
              required
              type="email"
              //InputProps={{ readOnly: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Date of Birth"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
              fullWidth
              required
              type="date"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Membership Category</InputLabel>
              <Select
                name="membershipCategory"
                value={formData.membershipCategory}
                onChange={handleChange}
                label="Membership Category"
                
                required
              >
                <MenuItem value="Individual Annual">Individual Annual</MenuItem>
                <MenuItem value="Individual (5 Years)">Individual (5 Years)</MenuItem>
                <MenuItem value="Student">Student</MenuItem>
                <MenuItem value="Institutional">Institutional</MenuItem>
                <MenuItem value="Industry/Corporate">Industry/Corporate</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Membership Start Date"
              name="membershipStartDate"
              value={formData.membershipStartDate}
              onChange={handleChange}
              fullWidth
              required
              type="date"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Resi Address (Line 1)"
              name="resiAddress1"
              value={formData.resiAddress1}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Resi Address (Line 2)"
              name="resiAddress2"
              value={formData.resiAddress2}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Pincode"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              type="number"
              fullWidth
              required
              sx={{ mb: 2 }}
            />

            {/* Gender Selection */}
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Gender</FormLabel>
              <RadioGroup
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                row
              >
                <FormControlLabel
                  value="Male"
                  control={<Radio />}
                  label="Male"
                />
                <FormControlLabel
                  value="Female"
                  control={<Radio />}
                  label="Female"
                />
                <FormControlLabel
                  value="Do not want to specify"
                  control={<Radio />}
                  label="Do not want to specify"
                />
              </RadioGroup>
            </FormControl>

            {/* Qualification and Organisation */}
            <TextField
              label="Qualification (Starting with Highest)"
              name="qualification"
              value={formData.qualification}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Organisation"
              name="organisation"
              value={formData.organisation}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="No of Employees in Organisation"
              name="noOfEmployees"
              value={formData.noOfEmployees}
              onChange={handleChange}
              type="number"
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Designation"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Total Experience (in years)"
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              type="number"
              fullWidth
              sx={{ mb: 2 }}
            />

            {/* Expertise Area Dropdown */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Area of Expertise/Work</InputLabel>
              <Select
                name="expertise"
                value={formData.expertise}
                onChange={handleChange}
                label="Area of Expertise/Work"
                required
              >
                <MenuItem value="HR/Admin">HR/Admin</MenuItem>
                <MenuItem value="Finance/Accts">Finance/Accts</MenuItem>
                <MenuItem value="Marketing/Sales">Marketing/Sales</MenuItem>
                <MenuItem value="Materials/Supply Chain">Materials/Supply Chain</MenuItem>
                <MenuItem value="Operations/Manufacturing">Operations/Manufacturing</MenuItem>
                <MenuItem value="IT/ITES">IT/ITES</MenuItem>
                <MenuItem value="Agriculture">Agriculture</MenuItem>
                <MenuItem value="Hospitality">Hospitality</MenuItem>
                <MenuItem value="Healthcare">Healthcare</MenuItem>
                <MenuItem value="Travel/Tourism">Travel/Tourism</MenuItem>
                <MenuItem value="Trading/Exports">Trading/Exports</MenuItem>
                <MenuItem value="Consultancy">Consultancy</MenuItem>
                <MenuItem value="Corporate Training">Corporate Training</MenuItem>
                <MenuItem value="Higher Education/Academics">Higher Education/Academics</MenuItem>
                <MenuItem value="School Education">School Education</MenuItem>
                <MenuItem value="Social Work">Social Work</MenuItem>
                <MenuItem value="Media/Entertainment">Media/Entertainment</MenuItem>
                <MenuItem value="Quality">Quality</MenuItem>
                <MenuItem value="Engineering">Engineering</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {/* Payment Amount */}
            <TextField
              label="Payment Amount"
              name="paymentAmount"
              value={formData.paymentAmount}
              onChange={handleChange}
              fullWidth
              type="number"
              sx={{ mb: 2 }}
            />

            {/* Payment Reference */}
            <TextField
              label="Payment Reference"
              name="paymentRef"
              value={formData.paymentRef}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />

            {/* Social Media Links */}
            <TextField
              label="LinkedIn Profile"
              name="linkedIn"
              value={formData.linkedIn}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Facebook Profile"
              name="facebook"
              value={formData.facebook}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Instagram Profile"
              name="instagram"
              value={formData.instagram}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />

            {/* Referral Source Dropdown */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>How Did You Come to Know About Us?</InputLabel>
              <Select
                name="referralSource"
                value={formData.referralSource}
                onChange={handleChange}
                label="How Did You Come to Know About Us?"
                required
              >
                <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                <MenuItem value="News paper">News paper</MenuItem>
                <MenuItem value="Social Media">Social Media</MenuItem>
                <MenuItem value="Referral from existing Member">
                  Referral from existing Member
                </MenuItem>
                <MenuItem value="Any other">Any other</MenuItem>
              </Select>
            </FormControl>

            {/* Additional Comments */}
            <TextField
              label="Additional Comments / Remarks"
              name="additionalComments"
              value={formData.additionalComments}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ mb: 2 }}
            />

            {/* Purpose of Joining */}
            <TextField
              label="Purpose of Joining"
              name="purposeOfJoining"
              value={formData.purposeOfJoining}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />

           <ReCAPTCHA
            ref={captchaRef} // Attach the ref to reCAPTCHA
            sitekey={process.env.REACT_APP_SITE_KEY}
            onChange={(value) => setCaptchaValue(value)}
            sx={{ mb: 2, width: "100%", mt:3 }}
          />

            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 3, mb:3 }}>
              Submit Request
            </Button>
          </form>
          

          {errorMessage && <Typography color="error">{errorMessage}</Typography>}
      </Paper>
    </Box>
  );
}

export default AddMemberForm;